import React, { useState } from 'react'
import styled from 'styled-components'
import { usersApi } from '../api'
import useFetch from '../hooks/useFetch'
import {
  Page, PageHead, Card, TableWrapper, Table, StatusPill,
  Loading, ErrorState, EmptyState, Input, Select, Button,
} from '../Components/ui'
import { colors, bp } from '../theme'
import { getUser } from '../auth'

const Bar = styled.div`
  margin-bottom: 1.25rem;
  h3 { margin: 0; color: ${colors.ink}; font-size: 1.15rem; }
`

const Form = styled.form`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;

  input { min-width: 180px; }

  @media (max-width: ${bp.mobile}) {
    input, select, button { width: 100%; }
  }
`

const Msg = styled.div`
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
  color: ${({ ok }) => (ok ? colors.greenDark : colors.red)};
`

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—')

const Users = () => {
  const { data: users, loading, error, reload } = useFetch(() => usersApi.list(), [])
  const me = getUser()
  const isAdmin = me?.role === 'admin'

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState('user')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  const list = users || []

  const create = async (e) => {
    e.preventDefault()
    if (!username.trim() || !password) return setMsg({ ok: false, text: 'Username and password are required.' })
    setSaving(true)
    setMsg(null)
    try {
      await usersApi.create({ username: username.trim(), password, role })
      setUsername(''); setPassword(''); setRole('user')
      setMsg({ ok: true, text: 'User created.' })
      reload()
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Could not create user.' })
    } finally {
      setSaving(false)
    }
  }

  // 403 means authenticated but not an admin — a permission state, not an error.
  const forbidden = error && error.status === 403

  return (
    <Page>
      <PageHead>
        <h1>Users</h1>
        <p>Manage dashboard accounts and roles.</p>
      </PageHead>

      {loading && <Card><Loading label="Loading users…" /></Card>}

      {forbidden && (
        <Card>
          <EmptyState title="Admins only"
            message="Your account doesn't have permission to view or manage users." />
        </Card>
      )}

      {error && !forbidden && <Card><ErrorState message={error.message} onRetry={reload} /></Card>}

      {!loading && !error && (
        <Card>
          {isAdmin && (
            <>
              <Bar><h3>Add a user</h3></Bar>
              {msg && <Msg ok={msg.ok}>{msg.text}</Msg>}
              <Form onSubmit={create}>
                <Input placeholder="Username" value={username}
                  onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
                <Input placeholder="Password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                <Select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </Select>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Add User'}
                </Button>
              </Form>
            </>
          )}

          <Bar><h3>All users</h3></Bar>
          {list.length === 0 ? (
            <EmptyState title="No users" message="Create the first dashboard user above." />
          ) : (
            <TableWrapper>
              <Table min="520px">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((u) => (
                    <tr key={u.id}>
                      <td>{u.username}{me?.username === u.username ? ' (you)' : ''}</td>
                      <td>
                        <StatusPill
                          state={u.role === 'admin' ? 'success' : 'idle'}
                          label={u.role}
                        />
                      </td>
                      <td>{fmtDate(u.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </Card>
      )}
    </Page>
  )
}

export default Users
